import {
  computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, reactive, ref, toRefs,
} from 'vue';
import { track } from '../tracking/trackingLayer';
import EventBus from '../../scripts/lib/EventBus.js';
import { debounceFn, readJsonFromDom } from '../../scripts/utils.js';
import RecoTracking from '../tracking/recoTracking.ts';

const TRACKING_ID_MIX_AND_MATCH = 'mix_match';
const TRACKING_ID_PRODUCT_LINE = 'linie';
const TRACKING_ID_CAPSULE = 'capsule';
const TRACKING_RULE_FASHION = 'fashion';
const TRACKING_RULE_BEAUTY = 'beauty';

const classProductFamilyHidden = 'ents-product-family--hidden';

const templateElement = document.getElementById('ents-product-family-template');

export { createApp } from 'vue';

export default ($root, $gridRow) => ({
  template: templateElement ? templateElement.innerHTML : '#ents-product-family',

  delimiters: ['${', '}'],

  components: {
    'product-family-product': defineAsyncComponent(() => import('./product-family-product.vue.component')),
  },

  setup() {
    const entsProductFamilyCarousel = ref(null);

    const state = reactive({
      url: null,
      initialized: false,
      visible: false,
      sliderAjaxResponse: {},
      trackingType: TRACKING_ID_MIX_AND_MATCH,
      trackingFashionBeauty: TRACKING_RULE_FASHION,
      mandatedHost: '/',
      wishlistApiTimeout: 1000,
      trackingStaticData: {},
    });

    const recoTracking = ref(null);

    const restartTracking = debounceFn(() => {
      if (recoTracking.value) {
        recoTracking.value.unobserve();
      }
      recoTracking.value = new RecoTracking(
        entsProductFamilyCarousel.value.querySelector('.ents-product-family__carousel'),
        '[data-is-slide]',
        {
          currency: {
            code: state.trackingStaticData.waehrung,
          },
          promotion_data: {
            type: 'slider',
            name: 'product_family',
            placement: 'page',
            rule: trackingRule.value,
          },
          event_data: {
            is_interaction_event: false,
          },
        },
      );

      recoTracking.value.observe();
    }, 100);

    function urlUpdate(url) {
      if (state.url === url) {
        return;
      }
      state.url = url;
      state.initialized = false;
      if (state.visible) {
        initialize();
      }
    }

    onMounted(() => {
      EventBus.on('ents:vue:product-family:update', urlUpdate);
      EventBus.emit('ents:vue:product-family:created');

      // cannot use vue prop for that because this component is the root vue instance which does not support receiving props
      const beautyAttribute = $root.dataset.beauty;
      if (beautyAttribute && beautyAttribute === 'true') {
        state.trackingFashionBeauty = TRACKING_RULE_BEAUTY;
      }
      state.trackingStaticData = readJsonFromDom('#entsEventTrackingData');

      const options = {
        root: null,
        rootMargin: '10px',
        threshold: 0,
      };
      const observer = new IntersectionObserver((entries) => {
        state.visible = entries[0].isIntersecting;
        if (!state.initialized && state.visible) {
          initialize();
        }
      }, options);
      observer.observe(document.querySelector('.ents-reco-container__grid-row'));

      state.mandatedHost = $root.dataset.mandatedHost;
      state.wishlistApiTimeout = parseInt($root.dataset.wishlistApiTimeout, 10);

      window.addEventListener('pageshow', onPageShow);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('pageshow', onPageShow);
    });

    function initialize() {
      if (state.url) {
        fetchData(state.url);
      } else {
        hideSlider();
      }
    }

    function fetchData(fetchUrl) {
      state.initialized = true;
      showSlider();
      const httpStatusOk = 200;

      fetch(fetchUrl)
        .catch(() => hideSlider())
        .then(res => (res.status === httpStatusOk ? res.json() : undefined))
        .then((json) => {
          if (json) {
            state.sliderAjaxResponse = json;
            updateSlider();
          } else {
            hideSlider();
          }
        });
    }

    async function fetchWishlistItems() {
      try {
        const requestItems = state.sliderAjaxResponse.matches.map((m) => {
          return {syan: m.code, colorId: m.color};
        });

        const response = await fetch(`${state.mandatedHost}ents/api/v1/wishlist/contains`, {
          signal: AbortSignal.timeout(state.wishlistApiTimeout),
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(requestItems),
          credentials: 'include'
        });

        const responseData = await response.json();
        const wishlistedItems = new Set(responseData.map(i => `${i.syan.toString()}${i.colorId ? '-' : ''}${i.colorId ?? ''}`));
        state.sliderAjaxResponse.matches = state.sliderAjaxResponse.matches.map((match) => {
          const matchId = `${match.code}${match.color ? '-' : ''}${match.color ?? ''}`;
          return {
            ...match,
            isOnWishlist: wishlistedItems.has(matchId)
          };
        });
      } catch (e) { // eslint-disable-line @typescript-eslint/no-unused-vars
        // timeout or invalid data / response json
      }
    }

    function hideSlider() {
      $root.classList.add(classProductFamilyHidden);
      $gridRow.setAttribute('data-ents-product-family-slide-count', 0);
    }

    function showSlider() {
      $root.classList.remove(classProductFamilyHidden);
    }

    function updateSlider() {
      $gridRow.setAttribute('data-ents-product-family-slide-count', state.sliderAjaxResponse.matches.length);
      if(state.sliderAjaxResponse.matches && state.sliderAjaxResponse.matches.length > 0){
        nextTick(() => {
          updateTrackingData();
          impressionTracking();
          initializeCAMPFlags();
          fetchWishlistItems();
          const carouselSelector = document.querySelector('ents-carousel.ents-product-family__carousel');
          carouselSelector.firstUpdated();
        });
      }
    }

    function updateTrackingData() {
      switch (state.sliderAjaxResponse.type) {
        case 'CAPSULE':
          state.trackingType = TRACKING_ID_CAPSULE;
          break;
        case 'MIX_AND_MATCH':
          state.trackingType = TRACKING_ID_MIX_AND_MATCH;
          break;
        default:
          state.trackingType = TRACKING_ID_PRODUCT_LINE;
      }
    }

    function impressionTracking() {
      track({
        reco_id: state.trackingType,
        reco_location: 'pds',
        reco_rule: trackingRule.value,
        event: 'reco.impression',
      });

      restartTracking();
    }

    function onPageShow(e) {
      if (e.persisted) {
        if (recoTracking.value) {
          restartTracking();
        }
        fetchWishlistItems();
      }
    }

    function initializeCAMPFlags() {
      const campPageRenderedEvent = new CustomEvent('camp:aktionsflags:initialized');
      document.dispatchEvent(campPageRenderedEvent);
    }

    const trackingRule = computed(() => `${state.trackingType}.${state.trackingFashionBeauty}`);

    return {
      ...toRefs(state),
      entsProductFamilyCarousel,
      initialize,
      trackingRule,
    };
  },
});
